import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #removeRefreshTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleFinishUpsellClick);
    this.addEventListener('click', this.#handleCartLineRemoveClick);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleFinishUpsellClick);
    this.removeEventListener('click', this.#handleCartLineRemoveClick);

    this.#historyAbortController?.abort();

    if (this.#removeRefreshTimeout) {
      window.clearTimeout(this.#removeRefreshTimeout);
    }
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();

    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartLineRemoveClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (!target?.closest('.cart-items__remove')) return;

    if (this.#removeRefreshTimeout) {
      window.clearTimeout(this.#removeRefreshTimeout);
    }

    this.#removeRefreshTimeout = window.setTimeout(() => {
      this.#refreshDrawer().catch((error) => console.error(error));
    }, 900);
  };

  #handleFinishUpsellClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (!target) return;

    const swatch = target.closest('[data-finish-swatch]');

    if (swatch) {
      if (/** @type {HTMLButtonElement} */ (swatch).disabled) return;

      this.#selectFinishSwatch(/** @type {HTMLElement} */ (swatch));
      return;
    }

    const button = target.closest('[data-cart-finish-add]');

    if (!button) return;

    event.preventDefault();

    await this.#addSelectedFinish(/** @type {HTMLButtonElement} */ (button));
  };

  #selectFinishSwatch(swatch) {
    const card = swatch.closest('[data-cart-finish-card]');

    if (!card) return;

    const finishName = swatch.dataset.colourName || swatch.dataset.productTitle || 'Finish';
    const productTitle = swatch.dataset.productTitle || finishName;
    const variantId = swatch.dataset.variantId || '';
    const image = swatch.dataset.image || '';
    const price = swatch.dataset.price || '';

    card.querySelectorAll('[data-finish-swatch]').forEach((item) => {
      const isSelected = item === swatch;

      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const input = card.querySelector('[data-cart-finish-variant-id]');
    if (input) input.value = variantId;

    const label = card.querySelector('[data-finish-colour-label]');
    if (label) label.textContent = productTitle;

    const addButton = card.querySelector('[data-cart-finish-add]');
    if (addButton) {
      addButton.textContent = `ADD ${productTitle.toUpperCase()} FINISH · +${price}`;
    }

    this.#updateFinishHero(card, image, productTitle);

    if (card.dataset.syncMainGallery === 'true' && image) {
      this.#syncMainProductGallery(image, productTitle, card.dataset.mainProductId || '');
    }
  }

  #updateFinishHero(card, image, finishName) {
    const hero = card.querySelector('[data-finish-hero]');

    if (!hero) return;

    if (!image) {
      hero.innerHTML = '';

      const label = document.createElement('span');
      label.className = 'hot-tub-finish__hero-label';
      label.textContent = finishName;

      hero.appendChild(label);
      return;
    }

    let nextSrc = image;

    try {
      nextSrc = new URL(image, window.location.origin).href;
    } catch (error) {
      nextSrc = image;
    }

    const currentImage = hero.querySelector('img');

    if (currentImage && currentImage.src === nextSrc) {
      return;
    }

    hero.classList.add('is-loading');

    const nextImage = document.createElement('img');
    nextImage.src = nextSrc;
    nextImage.alt = finishName;
    nextImage.loading = 'eager';
    nextImage.decoding = 'async';

    nextImage.addEventListener('load', () => {
      hero.classList.remove('is-loading');
    });

    nextImage.addEventListener('error', () => {
      hero.classList.remove('is-loading');
    });

    hero.innerHTML = '';
    hero.appendChild(nextImage);
  }

  #syncMainProductGallery(image, finishName, mainProductId = '') {
    if (!image || !mainProductId) return;

    const currentProductInput = document.querySelector('form[action*="/cart/add"] input[name="product-id"]');
    const currentProductId = currentProductInput?.value || '';

    if (!currentProductId || String(mainProductId) !== String(currentProductId)) {
      return;
    }

    let nextSrc = image;

    try {
      nextSrc = new URL(image, window.location.origin).href;
    } catch (error) {
      nextSrc = image;
    }

    const selectors = [
      'media-gallery .product__media-item.is-active img',
      'media-gallery .product__media-item:first-child img',
      '.product__media-item.is-active img',
      '.product__media-item:first-child img',
      '.product-media-container img',
      '.product__media img',
      '.product-gallery img',
      '[data-product-media] img'
    ];

    let mainImage = null;

    for (const selector of selectors) {
      mainImage = document.querySelector(selector);

      if (mainImage) break;
    }

    if (!mainImage) return;

    mainImage.src = nextSrc;
    mainImage.removeAttribute('srcset');
    mainImage.alt = finishName;

    const picture = mainImage.closest('picture');

    if (picture) {
      picture.querySelectorAll('source').forEach((source) => {
        source.removeAttribute('srcset');
      });
    }
  }

  async #addSelectedFinish(button) {
    const card = button.closest('[data-cart-finish-card]');

    if (!card) return;

    const input = card.querySelector('[data-cart-finish-variant-id]');
    const variantId = Number(input?.value || 0);
    const finishName = card.querySelector('[data-finish-colour-label]')?.textContent?.trim() || 'Finish';
    const originalText = button.textContent || 'Add finish';

    const mainProductId = card.dataset.mainProductId || '';
    const mainProductHandle = card.dataset.mainProductHandle || '';
    const mainProductTitle = card.dataset.mainProductTitle || '';

    if (!variantId) {
      button.textContent = 'Select a finish';

      setTimeout(() => {
        button.textContent = originalText;
      }, 1800);

      return;
    }

    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            Finish: finishName,
            _cart_addon: 'finish',
            _main_product_id: mainProductId,
            _main_product_handle: mainProductHandle,
            _main_product_title: mainProductTitle,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.description || 'Could not add finish');
      }

      button.textContent = 'Added';

      await this.#refreshDrawer();
      this.showDialog();
    } catch (error) {
      console.error(error);

      button.disabled = false;
      button.textContent = 'Try again';

      setTimeout(() => {
        button.textContent = originalText;
      }, 1800);
    }
  }

  async #refreshDrawer() {
    const sectionId = this.querySelector('cart-items-component[data-section-id]')?.dataset.sectionId;

    if (!sectionId) {
      window.location.reload();
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Could not refresh cart drawer');
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const nextInner =
      doc.querySelector('cart-drawer-component .cart-drawer__inner') ||
      doc.querySelector('.cart-drawer__inner');

    const currentInner = this.querySelector('.cart-drawer__inner');

    if (nextInner && currentInner) {
      currentInner.innerHTML = nextInner.innerHTML;
    }

    const nextTrigger = doc.querySelector('[data-testid="cart-drawer-trigger"]');
    const currentTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');

    if (nextTrigger && currentTrigger) {
      currentTrigger.innerHTML = nextTrigger.innerHTML;
    }

    this.#updateStickyState();
  }

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener('click', this.closeDialog, {
        once: true,
      });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}